import React from 'react';
import { motion } from 'framer-motion';

const QuienesSomos = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <motion.section
        className="bg-custom-blue text-white py-20"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="max-w-4xl mx-auto px-8">
          <motion.h1
            className="text-4xl md:text-6xl font-serif font-bold text-center mb-6"
            variants={itemVariants}
          >
            ¿Quiénes Somos?
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-center leading-relaxed"
            variants={itemVariants}
          >
            Conoce más sobre nuestra misión de democratizar el acceso a la justicia
          </motion.p>
        </div>
      </motion.section>

      {/* Main Content */}
      <motion.section
        className="py-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="max-w-4xl mx-auto px-8">
          <div className="space-y-12">
            <motion.div variants={itemVariants}>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
                Nuestra Misión
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                En Iustia, creemos que todos merecen acceso a servicios legales de calidad, sin
                importar su ubicación o circunstancias económicas. Nuestra plataforma conecta a
                personas que necesitan asesoría legal con abogados especializados y verificados,
                creando un puente entre la justicia y quienes la buscan.
              </p>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
                Nuestra Visión
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Ser la plataforma líder en servicios legales digitales en América Latina,
                transformando la manera en que las personas acceden a la justicia a través de la
                tecnología, la transparencia y la eficiencia.
              </p>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
                Nuestros Valores
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-custom-blue">Transparencia</h3>
                  <p className="text-gray-700">
                    Operamos con total claridad en nuestros procesos, precios y servicios.
                  </p>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-custom-blue">Eficiencia</h3>
                  <p className="text-gray-700">
                    Optimizamos cada paso para brindarte resultados rápidos y efectivos.
                  </p>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-custom-blue">Accesibilidad</h3>
                  <p className="text-gray-700">
                    Hacemos que los servicios legales sean accesibles para todos.
                  </p>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-custom-blue">Calidad</h3>
                  <p className="text-gray-700">
                    Trabajamos solo con abogados certificados y especializados.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
                ¿Por Qué Elegir Iustia?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-2 h-2 bg-custom-blue rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Abogados Verificados
                    </h4>
                    <p className="text-gray-700">
                      Todos nuestros profesionales pasan por un riguroso proceso de verificación de
                      credenciales y experiencia.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-2 h-2 bg-custom-blue rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Tecnología Avanzada
                    </h4>
                    <p className="text-gray-700">
                      Utilizamos las últimas tecnologías para brindarte una experiencia fluida y
                      segura en todos nuestros servicios.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-2 h-2 bg-custom-blue rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Soporte 24/7</h4>
                    <p className="text-gray-700">
                      Nuestro equipo de soporte está disponible las 24 horas para ayudarte cuando lo
                      necesites.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        className="bg-gray-50 py-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="max-w-4xl mx-auto px-8 text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6"
            variants={itemVariants}
          >
            ¿Listo para comenzar?
          </motion.h2>
          <motion.p className="text-lg text-gray-700 mb-8" variants={itemVariants}>
            Únete a miles de usuarios que ya confían en Iustia para sus necesidades legales
          </motion.p>
          <motion.div className="space-x-4" variants={itemVariants}>
            <button className="bg-custom-blue text-white px-8 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors duration-200">
              Buscar Abogado
            </button>
            <button className="border-2 border-custom-blue text-custom-blue px-8 py-3 rounded-full font-medium hover:bg-custom-blue hover:text-white transition-colors duration-200">
              Consulta Express
            </button>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default QuienesSomos;
